"use client"

import { Link } from "react-router-dom"

const HelpPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30 text-center mb-6">
          <img src="/New_Logo.png" alt="NeuroTrain Logo" className="mx-auto h-12 mb-4" />
          <h1 className="text-3xl font-bold text-[#0A4B4B] mb-2">Help & Support</h1>
          <p className="text-gray-600">Get help with NeuroTrain Call Analyzer</p>
        </div>

        {/* Contact Information */}
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-5 h-5 mr-2 text-[#0A4B4B]">❓</span>
            Need Immediate Help?
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200">
              <span className="w-6 h-6 text-green-600 mr-3 text-xl">📞</span>
              <div>
                <h3 className="font-semibold text-green-800">USA Toll-Free Hotline</h3>
                <a href="tel:888-706-0080" className="text-green-700 hover:text-green-900 font-mono text-lg">
                  888-706-0080
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <span className="w-6 h-6 text-blue-600 mr-3 text-xl">📧</span>
              <div>
                <h3 className="font-semibold text-blue-800">Email Support</h3>
                <a href="mailto:felipe@patagonusa.com" className="text-blue-700 hover:text-blue-900">
                  felipe@patagonusa.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting Sections */}
        <div className="space-y-6">
          {/* File Compatibility Issues */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="w-5 h-5 mr-2 text-red-500 text-xl">❌</span>
              File Not Compatible
            </h2>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">Supported File Formats</h3>
                <ul className="text-red-700 space-y-1">
                  <li>
                    • <strong>Audio Files:</strong> .mp3, .wav
                  </li>
                  <li>
                    • <strong>Maximum Size:</strong> 50MB per file
                  </li>
                  <li>
                    • <strong>Duration:</strong> Up to 60 minutes
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Common Solutions:</h3>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      1
                    </span>
                    <span>
                      <strong>Convert your file:</strong> Use online converters to change your file to .mp3 or .wav
                      format
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      2
                    </span>
                    <span>
                      <strong>Check file size:</strong> Compress large files or split long recordings
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      3
                    </span>
                    <span>
                      <strong>Clear audio quality:</strong> Ensure the recording is clear and audible
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Evaluation Not Loading */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="w-5 h-5 mr-2 text-yellow-500 text-xl">⚠️</span>
              Evaluation Not Loading
            </h2>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">Processing Time</h3>
                <p className="text-yellow-700">
                  Analysis typically takes 2-5 minutes depending on file size and server load.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Troubleshooting Steps:</h3>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      1
                    </span>
                    <span>
                      <strong>Wait patiently:</strong> Large files may take up to 10 minutes to process
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      2
                    </span>
                    <span>
                      <strong>Check your connection:</strong> Ensure stable internet connection
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      3
                    </span>
                    <span>
                      <strong>Refresh the page:</strong> If stuck for more than 10 minutes, refresh and try again
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      4
                    </span>
                    <span>
                      <strong>Try a smaller file:</strong> Test with a shorter recording first
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Email Issues */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="w-5 h-5 mr-2 text-blue-500 text-xl">📧</span>
              Email Not Sending
            </h2>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Email Delivery</h3>
                <p className="text-blue-700">
                  Reports are sent instantly but may take a few minutes to appear in your inbox.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Common Solutions:</h3>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      1
                    </span>
                    <span>
                      <strong>Check spam folder:</strong> Reports may be filtered as spam
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      2
                    </span>
                    <span>
                      <strong>Verify email address:</strong> Ensure you entered the correct email
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      3
                    </span>
                    <span>
                      <strong>Use PDF download:</strong> Alternative option to get your report
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-[#0A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">
                      4
                    </span>
                    <span>
                      <strong>Try different email:</strong> Test with another email address
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* General Tips */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="w-5 h-5 mr-2 text-green-500 text-xl">💡</span>
              General Tips for Best Results
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Audio Quality</h3>
                <ul className="text-gray-700 space-y-1 text-sm">
                  <li>• Clear, audible speech</li>
                  <li>• Minimal background noise</li>
                  <li>• Good microphone quality</li>
                  <li>• Avoid echo or distortion</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Best Practices</h3>
                <ul className="text-gray-700 space-y-1 text-sm">
                  <li>• Fill in agent name accurately</li>
                  <li>• Select correct call type</li>
                  <li>• Use stable internet connection</li>
                  <li>• Keep browser tab active during upload</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Analyzer */}
        <div className="text-center mt-8">
          <Link
            to="/analyzer"
            className="inline-flex items-center bg-[#0A4B4B] hover:bg-[#083d3d] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            ← Back to Analyzer
          </Link>
        </div>
      </div>
    </div>
  )
}

export default HelpPage
